import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../auth/AuthProvider';
import { usePdfCourseCreation } from '../hooks/usePdfCourseCreation';

const MAX_FILE_SIZE_MB = 25;
const ACCEPTED_TYPES = ['application/pdf'];

const DIFFICULTIES = [
  { value: '', label: 'Auto-detect' },
  { value: 'BEGINNER', label: 'Beginner' },
  { value: 'INTERMEDIATE', label: 'Intermediate' },
  { value: 'ADVANCED', label: 'Advanced' },
];

const STATUS_LABELS = {
  QUEUED: 'Queued',
  EXTRACTING_TEXT: 'Extracting text from PDF',
  ANALYZING_CONTENT: 'Analyzing content',
  GENERATING_OUTLINE: 'Generating course outline',
  CREATING_COURSE: 'Creating your course',
  // Emitted only by the SSE stream (the poll status enum stops at CREATING_COURSE).
  GENERATING_FIRST_LESSON: 'Writing your first lesson',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
};

const STATUS_STEPS = [
  'QUEUED',
  'EXTRACTING_TEXT',
  'ANALYZING_CONTENT',
  'GENERATING_OUTLINE',
  'CREATING_COURSE',
  'GENERATING_FIRST_LESSON',
];

export default function PdfUploadModal({ isOpen, onClose, studentId, onSuccessNavigate, onError: onErrorProp }) {
  const navigate = useNavigate();
  const { enabled: authEnabled } = useAuth();
  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [title, setTitle] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [courseDomain, setCourseDomain] = useState('');
  const [expectedOutcomes, setExpectedOutcomes] = useState('');
  const [fileError, setFileError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const {
    isUploading,
    isProcessing,
    status,
    progress,
    message,
    error,
    reset,
    uploadPdfStream,
  } = usePdfCourseCreation({
    studentId,
    onComplete: (courseId, firstLessonId) => {
      // Navigate to the destination after a short delay for success state.
      setTimeout(() => {
        if (typeof onSuccessNavigate === 'function') {
          onSuccessNavigate({ courseId, firstLessonId });
        } else if (firstLessonId) {
          navigate(`/learn/${courseId}/${firstLessonId}`);
        } else {
          navigate(`/courses/${courseId}`);
        }
        handleClose();
      }, 1500);
    },
    onError: (err) => {
      // Error is already reflected in local state; also bubble the raw
      // Error up so parent pages (e.g. StudentAssignCoursePage) can branch
      // on err.code (e.g. STUDENT_SUBSCRIPTION_REQUIRED).
      if (process.env.NODE_ENV !== 'production') {
        console.error('PDF upload error:', err);
      }
      onErrorProp?.(err);
    },
  });

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setTitle('');
      setDifficulty('');
      setCourseDomain('');
      setExpectedOutcomes('');
      setFileError(null);
      setShowAdvanced(false);
      reset();
    }
  }, [isOpen, reset]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isUploading && !isProcessing) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isUploading, isProcessing, onClose]);

  const handleClose = () => {
    if (!isUploading && !isProcessing) {
      onClose();
    }
  };

  const validateFile = (file) => {
    if (!ACCEPTED_TYPES.includes(file.type) && !file.name.endsWith('.pdf')) {
      return 'Please select a PDF file.';
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `File size must be less than ${MAX_FILE_SIZE_MB}MB.`;
    }
    return null;
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setFileError(null);

    if (file) {
      const validationError = validateFile(file);
      if (validationError) {
        setFileError(validationError);
        setSelectedFile(null);
      } else {
        setSelectedFile(file);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    setFileError(null);

    const file = e.dataTransfer.files[0];
    if (file) {
      const validationError = validateFile(file);
      if (validationError) {
        setFileError(validationError);
        setSelectedFile(null);
      } else {
        setSelectedFile(file);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile || isUploading || isProcessing) return;

    // Self-serve streams live progress via SSE; parent assignment (studentId
    // set) has no stream endpoint, so uploadPdfStream delegates to the
    // non-streaming upload + poll flow internally.
    await uploadPdfStream(
      selectedFile,
      {
        title: title.trim() || undefined,
        difficulty: difficulty || undefined,
        courseDomain: courseDomain.trim() || undefined,
        expectedOutcomes: expectedOutcomes.trim() || undefined,
      },
      authEnabled
    );
  };

  const handleDismissToBackground = () => {
    onClose();
  };

  const handleRetry = () => {
    reset();
    setFileError(null);
  };

  const currentStepIndex = status ? STATUS_STEPS.indexOf(status) : -1;

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        .pdf-modal-overlay {
          position: fixed;
          inset: 0;
          background: var(--overlay-bg);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: pdfFadeIn 0.2s ease;
          padding: var(--space-4);
        }

        @keyframes pdfFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes pdfScaleIn {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }

        /* Shape mirrors the shared parent-portal Modal.jsx primitive
           (bg-elevated / radius-overlay / shadow-lg) so this reads as the
           same modal system, not a bespoke one. */
        .pdf-modal {
          background: var(--bg-elevated);
          border: 1px solid var(--border-subtle);
          border-radius: var(--radius-overlay);
          width: 100%;
          max-width: 520px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: var(--shadow-lg);
          animation: pdfScaleIn 0.25s ease;
        }

        .pdf-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--space-4) var(--space-5) var(--space-3);
          border-bottom: 1px solid var(--border-subtle);
        }

        .pdf-modal-title {
          font-size: var(--text-panel-size);
          font-weight: var(--text-panel-weight);
          color: var(--text-primary);
          margin: 0;
        }

        .pdf-modal-close {
          width: 32px;
          height: 32px;
          border-radius: var(--radius-control);
          border: none;
          background: transparent;
          color: var(--text-secondary);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s ease, color 0.15s ease;
        }

        .pdf-modal-close:hover {
          background: var(--glass-bg-medium);
          color: var(--text-primary);
        }

        .pdf-modal-close:focus-visible {
          outline: none;
          box-shadow: var(--focus-ring);
        }

        .pdf-modal-body {
          padding: var(--space-4) var(--space-5);
        }

        .pdf-drop-zone {
          border: 2px dashed var(--border-subtle);
          border-radius: var(--radius-panel);
          padding: var(--space-6) var(--space-5);
          text-align: center;
          cursor: pointer;
          transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
          background: var(--glass-bg-light);
        }

        .pdf-drop-zone:hover {
          border-color: var(--border-hover);
        }

        /* Active drag target — an accent ring here mirrors the same
           --focus-ring / --input-focus-border treatment inputs use, not
           decoration (spec §2 nuance). */
        .pdf-drop-zone.dragging {
          border-color: var(--accent-primary);
          background: var(--accent-light);
          box-shadow: var(--focus-ring);
        }

        /* File accepted / rejected are completion / error signals, so
           colour is legitimate here (spec §2). */
        .pdf-drop-zone.has-file {
          border-color: var(--success);
          background: var(--success-bg);
        }

        .pdf-drop-zone.has-error {
          border-color: var(--danger);
          background: var(--danger-bg);
        }

        .pdf-drop-icon {
          width: 48px;
          height: 48px;
          margin: 0 auto var(--space-4);
          color: var(--text-tertiary);
        }

        .pdf-drop-zone.has-file .pdf-drop-icon {
          color: var(--success);
        }

        .pdf-drop-text {
          font-size: var(--text-body-size);
          color: var(--text-secondary);
          margin: 0 0 var(--space-2) 0;
        }

        .pdf-drop-hint {
          font-size: var(--text-numeric-size);
          color: var(--text-tertiary);
          margin: 0;
        }

        .pdf-file-info {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: var(--space-2);
        }

        .pdf-file-name {
          font-size: var(--text-body-size);
          font-weight: 500;
          color: var(--text-primary);
        }

        .pdf-file-size {
          font-size: var(--text-numeric-size);
          color: var(--text-secondary);
        }

        .pdf-error-text {
          color: var(--danger);
          font-size: var(--text-numeric-size);
          margin-top: var(--space-3);
        }

        .pdf-form-fields {
          margin-top: var(--space-5);
        }

        .pdf-advanced-toggle {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          width: 100%;
          padding: var(--space-3) 0;
          background: transparent;
          border: none;
          color: var(--text-secondary);
          font-size: var(--text-body-size);
          font-weight: 500;
          cursor: pointer;
          transition: color 0.15s ease;
        }

        .pdf-advanced-toggle:hover {
          color: var(--text-primary);
        }

        .pdf-advanced-toggle:focus-visible {
          outline: none;
          box-shadow: var(--focus-ring);
        }

        .pdf-advanced-content {
          max-height: 0;
          overflow: hidden;
          opacity: 0;
          transition: all 0.3s ease;
        }

        .pdf-advanced-content.open {
          max-height: 400px;
          opacity: 1;
          margin-top: var(--space-2);
        }

        .pdf-field-group {
          margin-bottom: var(--space-4);
        }

        /* Form-field caption — same convention as field-label in
           CourseRequestForm and the LinkStudentModal label. */
        .pdf-field-label {
          display: block;
          font-size: var(--text-label-size);
          font-weight: var(--text-label-weight);
          letter-spacing: var(--text-label-tracking);
          text-transform: uppercase;
          color: var(--text-tertiary);
          margin-bottom: var(--space-2);
        }

        .pdf-field-input,
        .pdf-field-select,
        .pdf-field-textarea {
          width: 100%;
          padding: var(--space-3) var(--space-4);
          background: var(--input-bg);
          border: 1px solid var(--input-border);
          border-radius: var(--radius-control);
          color: var(--text-primary);
          font-size: var(--text-body-size);
          font-family: inherit;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
          outline: none;
        }

        .pdf-field-input:focus,
        .pdf-field-select:focus,
        .pdf-field-textarea:focus {
          border-color: var(--input-focus-border);
          box-shadow: var(--focus-ring);
        }

        .pdf-field-input::placeholder,
        .pdf-field-textarea::placeholder {
          color: var(--input-placeholder);
        }

        .pdf-field-textarea {
          resize: vertical;
          min-height: 64px;
        }

        .pdf-modal-footer {
          padding: var(--space-3) var(--space-5) var(--space-4);
          border-top: 1px solid var(--border-subtle);
          display: flex;
          gap: var(--space-2);
          justify-content: flex-end;
        }

        /* Padding/radius match the Button.jsx primitive's md size so these
           modal-footer buttons read like every other button in the app. */
        .pdf-btn {
          padding: var(--space-3) var(--space-4);
          border-radius: var(--radius-control);
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
        }

        .pdf-btn:focus-visible {
          outline: none;
          box-shadow: var(--focus-ring);
        }

        .pdf-btn-secondary {
          background: var(--btn-secondary-bg);
          border: 1px solid var(--btn-secondary-border);
          color: var(--btn-secondary-text);
        }

        .pdf-btn-secondary:hover {
          background: var(--btn-secondary-hover);
        }

        /* Create Course / Try Again are each dialog's one primary action
           (spec §2), so the accent fill is legitimate. */
        .pdf-btn-primary {
          background: var(--btn-primary-bg);
          border: 1px solid transparent;
          color: var(--btn-primary-text);
        }

        .pdf-btn-primary:hover:not(:disabled) {
          background: var(--btn-primary-hover);
        }

        .pdf-btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Processing state */
        .pdf-processing {
          text-align: center;
          padding: var(--space-6) var(--space-5);
        }

        .pdf-processing-spinner {
          width: 56px;
          height: 56px;
          margin: 0 auto var(--space-5);
        }

        /* Loading is an active progress signal, so the accent ring is
           legitimate (spec §2 nuance). */
        .pdf-spinner-ring {
          width: 100%;
          height: 100%;
          border: 3px solid var(--border-subtle);
          border-top-color: var(--accent-primary);
          border-radius: 50%;
          animation: pdfSpin 1s linear infinite;
        }

        @keyframes pdfSpin {
          to { transform: rotate(360deg); }
        }

        .pdf-processing-title {
          font-size: var(--text-section-size);
          font-weight: var(--text-section-weight);
          color: var(--text-primary);
          margin: 0 0 var(--space-2) 0;
        }

        .pdf-processing-message {
          font-size: var(--text-body-size);
          color: var(--text-secondary);
          margin: 0 0 var(--space-5) 0;
        }

        .pdf-progress-bar-container {
          height: 8px;
          background: var(--bg-tertiary);
          border-radius: 4px;
          overflow: hidden;
          margin-bottom: var(--space-5);
        }

        /* Flat token fill, not a gradient (CLAUDE.md bans decorative
           gradients) — the bar's own animated width already carries the
           progress signal. */
        .pdf-progress-bar {
          height: 100%;
          background: var(--accent-primary);
          border-radius: 4px;
          transition: width 0.3s ease;
        }

        .pdf-steps {
          display: flex;
          justify-content: space-between;
          margin-bottom: var(--space-5);
        }

        .pdf-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          flex: 1;
          position: relative;
        }

        .pdf-step:not(:last-child)::after {
          content: '';
          position: absolute;
          top: 12px;
          left: calc(50% + 16px);
          right: calc(-50% + 16px);
          height: 2px;
          background: var(--border-subtle);
        }

        .pdf-step.completed:not(:last-child)::after {
          background: var(--success);
        }

        .pdf-step-indicator {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: var(--bg-tertiary);
          border: 2px solid var(--border-subtle);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: var(--text-label-size);
          font-weight: 600;
          color: var(--text-tertiary);
          margin-bottom: var(--space-2);
          position: relative;
          z-index: 1;
        }

        /* Completed / active steps are progress signals, so colour is
           legitimate here (spec §2). */
        .pdf-step.completed .pdf-step-indicator {
          background: var(--success);
          border-color: var(--success);
          color: var(--text-inverted);
        }

        .pdf-step.active .pdf-step-indicator {
          background: var(--accent-primary);
          border-color: var(--accent-primary);
          color: var(--text-inverted);
          animation: pdfPulse 1.5s infinite;
        }

        @keyframes pdfPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        .pdf-step-label {
          font-size: var(--text-label-size);
          color: var(--text-tertiary);
          text-align: center;
          max-width: 60px;
        }

        .pdf-step.active .pdf-step-label,
        .pdf-step.completed .pdf-step-label {
          color: var(--text-secondary);
        }

        /* Informational aside, not a progress/completion signal itself —
           neutral surface (spec §2 sweep). */
        .pdf-background-notice {
          background: var(--bg-tertiary);
          border: 1px solid var(--border-subtle);
          border-radius: var(--radius-control);
          padding: var(--space-4);
          margin-top: var(--space-4);
        }

        .pdf-background-notice p {
          font-size: var(--text-body-size);
          color: var(--text-secondary);
          margin: 0 0 var(--space-3) 0;
          line-height: 1.5;
        }

        .pdf-background-notice strong {
          color: var(--text-primary);
        }

        /* Success state — completion, so green is legitimate (spec §2). */
        .pdf-success-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto var(--space-5);
          color: var(--success);
        }

        .pdf-success-icon svg {
          width: 100%;
          height: 100%;
        }

        /* Error state */
        .pdf-error-state {
          text-align: center;
          padding: var(--space-6) var(--space-5);
        }

        .pdf-error-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto var(--space-5);
          color: var(--danger);
        }

        .pdf-error-icon svg {
          width: 100%;
          height: 100%;
        }

        .pdf-error-title {
          font-size: var(--text-section-size);
          font-weight: var(--text-section-weight);
          color: var(--danger);
          margin: 0 0 var(--space-2) 0;
        }

        .pdf-error-message {
          font-size: var(--text-body-size);
          color: var(--text-secondary);
          margin: 0 0 var(--space-5) 0;
        }

        @media (max-width: 480px) {
          .pdf-modal {
            max-height: 85vh;
            border-radius: var(--radius-panel) var(--radius-panel) 0 0;
          }

          .pdf-modal-body {
            padding: var(--space-5) var(--space-4);
          }

          .pdf-steps {
            display: none;
          }

          .pdf-step-label {
            font-size: 10px;
          }
        }
      `}</style>

      <div className="pdf-modal-overlay" onClick={handleClose}>
        <div className="pdf-modal" onClick={(e) => e.stopPropagation()}>
          <div className="pdf-modal-header">
            <h2 className="pdf-modal-title">
              {isProcessing ? 'Creating Course' : 'Upload PDF'}
            </h2>
            {!isUploading && !isProcessing && (
              <button className="pdf-modal-close" onClick={handleClose} aria-label="Close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          {/* Error State */}
          {error && !isProcessing && (
            <div className="pdf-error-state">
              <div className="pdf-error-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <h3 className="pdf-error-title">Upload Failed</h3>
              <p className="pdf-error-message">{error.message}</p>
              <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'center' }}>
                <button className="pdf-btn pdf-btn-secondary" onClick={handleClose}>
                  Cancel
                </button>
                <button className="pdf-btn pdf-btn-primary" onClick={handleRetry}>
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* Processing State */}
          {(isUploading || isProcessing) && !error && status !== 'COMPLETED' && (
            <div className="pdf-processing">
              <div className="pdf-processing-spinner">
                <div className="pdf-spinner-ring" />
              </div>
              <h3 className="pdf-processing-title">
                {isUploading ? 'Uploading...' : STATUS_LABELS[status] || 'Processing...'}
              </h3>
              <p className="pdf-processing-message">
                {message || 'This may take a few minutes for larger documents.'}
              </p>

              <div className="pdf-progress-bar-container">
                <div className="pdf-progress-bar" style={{ width: `${progress}%` }} />
              </div>

              {!isUploading && (
                <div className="pdf-steps">
                  {STATUS_STEPS.map((step, index) => (
                    <div
                      key={step}
                      className={`pdf-step ${index < currentStepIndex ? 'completed' : ''} ${step === status ? 'active' : ''}`}
                    >
                      <div className="pdf-step-indicator">
                        {index < currentStepIndex ? '✓' : index + 1}
                      </div>
                      <span className="pdf-step-label">
                        {STATUS_LABELS[step]?.split(' ').slice(0, 2).join(' ')}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="pdf-background-notice">
                <p>
                  <strong>Your course is being generated in the background.</strong> You can close this modal and continue browsing. We'll redirect you when it's ready.
                </p>
                <button className="pdf-btn pdf-btn-secondary" onClick={handleDismissToBackground}>
                  Continue browsing
                </button>
              </div>
            </div>
          )}

          {/* Success State */}
          {status === 'COMPLETED' && (
            <div className="pdf-processing">
              <div className="pdf-success-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </div>
              <h3 className="pdf-processing-title" style={{ color: 'var(--success)' }}>
                Course Created!
              </h3>
              <p className="pdf-processing-message">
                Redirecting you to your new course...
              </p>
            </div>
          )}

          {/* Upload Form */}
          {!isUploading && !isProcessing && !error && (
            <>
              <form onSubmit={handleSubmit}>
                <div className="pdf-modal-body">
                  <div
                    className={`pdf-drop-zone ${isDragging ? 'dragging' : ''} ${selectedFile ? 'has-file' : ''} ${fileError ? 'has-error' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={handleFileChange}
                      hidden
                    />

                    <div className="pdf-drop-icon">
                      {selectedFile ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <path d="M9 15l2 2 4-4" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                      )}
                    </div>

                    {selectedFile ? (
                      <div className="pdf-file-info">
                        <span className="pdf-file-name">{selectedFile.name}</span>
                        <span className="pdf-file-size">
                          ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                    ) : (
                      <>
                        <p className="pdf-drop-text">
                          Drag and drop a PDF file, or click to browse
                        </p>
                        <p className="pdf-drop-hint">Maximum file size: {MAX_FILE_SIZE_MB}MB</p>
                      </>
                    )}
                  </div>

                  {fileError && <p className="pdf-error-text">{fileError}</p>}

                  <div className="pdf-form-fields">
                    <div className="pdf-field-group">
                      <label className="pdf-field-label" htmlFor="pdf-title">
                        Course Title (optional)
                      </label>
                      <input
                        type="text"
                        id="pdf-title"
                        className="pdf-field-input"
                        placeholder="Leave empty to auto-generate from PDF"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                      />
                    </div>

                    <button
                      type="button"
                      className="pdf-advanced-toggle"
                      onClick={() => setShowAdvanced(!showAdvanced)}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        style={{
                          transition: 'transform 0.2s ease',
                          transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                      Advanced options
                    </button>

                    <div className={`pdf-advanced-content ${showAdvanced ? 'open' : ''}`}>
                      <div className="pdf-field-group">
                        <label className="pdf-field-label" htmlFor="pdf-difficulty">
                          Difficulty Level
                        </label>
                        <select
                          id="pdf-difficulty"
                          className="pdf-field-select"
                          value={difficulty}
                          onChange={(e) => setDifficulty(e.target.value)}
                        >
                          {DIFFICULTIES.map((d) => (
                            <option key={d.value} value={d.value}>
                              {d.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="pdf-field-group">
                        <label className="pdf-field-label" htmlFor="pdf-domain">
                          Subject Area (optional)
                        </label>
                        <input
                          type="text"
                          id="pdf-domain"
                          className="pdf-field-input"
                          placeholder="e.g., Programming, Mathematics, History"
                          value={courseDomain}
                          onChange={(e) => setCourseDomain(e.target.value)}
                        />
                      </div>

                      <div className="pdf-field-group">
                        <label className="pdf-field-label" htmlFor="pdf-outcomes">
                          Learning Outcomes (optional)
                        </label>
                        <textarea
                          id="pdf-outcomes"
                          className="pdf-field-textarea"
                          placeholder="What should learners achieve after completing this course?"
                          value={expectedOutcomes}
                          onChange={(e) => setExpectedOutcomes(e.target.value)}
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pdf-modal-footer">
                  <button type="button" className="pdf-btn pdf-btn-secondary" onClick={handleClose}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="pdf-btn pdf-btn-primary"
                    disabled={!selectedFile}
                  >
                    Create Course
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </>
  );
}
